import { camelCase } from 'change-case'

import { AttributesDTO } from 'features/ab-tests/api/types/abTestsVariantsSchemaDTO'
import { Attributes } from 'features/ab-tests/types/abTestVariantsSchema'
import { Primitive } from 'types/primitive'
import { floatPercentFromDTO } from 'utils/number'
import { isPrimitive } from 'utils/primitive'

export const traverseMapAttributes = <T, R>(obj: T): R => {
  const traverse = (node: unknown): unknown => {
    if (Array.isArray(node)) {
      return node.map(traverse)
    } else if (typeof node === 'object' && node !== null) {
      const result: Record<string, unknown> = {}

      for (const [key, value] of Object.entries(node)) {
        if (isAbTestsVariantsAttributesDTO(value)) {
          try {
            result[key] = attributesFromDTO(value)
          } catch (error) {
            throw new Error(`Error mapping attributes at key "${key}": ${(error as Error).message}`)
          }
        } else {
          result[key] = traverse(value)
        }
      }

      return result
    } else {
      return node
    }
  }

  return traverse(obj) as R
}

const isAbTestsVariantsAttributesDTO = (value: unknown): value is AttributesDTO =>
  typeof value === 'object' &&
  value !== null &&
  'label' in value &&
  'type' in value &&
  'value' in value &&
  'workspaceDefaultValue' in value

const attributesFromDTO = (attributes: AttributesDTO): Attributes => {
  const { label, type, value, workspaceDefaultValue } = attributes

  if (!isPrimitive(type)) {
    throw new Error('Type of input does not match to primitive types.')
  }

  return {
    label,
    type,
    value: value !== null ? parsePrimitiveValue(value, type) : undefined,
    workspaceDefaultValue:
      workspaceDefaultValue !== null ? parsePrimitiveValue(workspaceDefaultValue, type) : undefined,
  }
}

export const parsePrimitiveValue = (value: unknown, type: Primitive): boolean | number | string => {
  let parsedValue: boolean | number | string

  switch (type) {
    case 'boolean': {
      parsedValue = value as boolean
      break
    }
    case 'enum': {
      parsedValue = camelCase(value as string)
      break
    }
    case 'float':
    case 'integer': {
      parsedValue = value as number
      break
    }
    case 'floatPercent': {
      parsedValue = floatPercentFromDTO(value as number)
      break
    }
    case 'json':
    case 'string': {
      parsedValue = value as string
      break
    }
    case 'stringCode': {
      parsedValue = value as string
      break
    }
  }

  return parsedValue
}
